import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import CitiesApi from '../lib/api/CitiesApi';

/**
 * Cities for every picker in the dashboard.
 *
 * Returns the list UNFILTERED and UNSORTED by default, in server order. That is
 * deliberate: of the pages this replaces, six show every city in server order
 * and five of those rely on non-operational cities being present — a retired
 * market still has to render for a driver whose home city it is, or the field
 * shows an empty chip. Filtering and sorting are opt-in for the two pages that
 * already did them.
 *
 * The query key ignores the options so every caller shares one cache entry and
 * one request, reproducing the session-wide Apollo cache-first singleton the
 * Redux slice was — including the DriverNotifications page, which calls this
 * hook twice with different options and must still cause exactly one request.
 */
const useCities = ({ operationalOnly = false, sorted = false } = {}) => {
  const { data, isLoading, error } = useQuery({
    queryKey: ['cities'],
    queryFn: () => CitiesApi.getCities(),
    staleTime: Infinity,
  });

  const cities = useMemo(() => {
    let list = data ?? [];
    if (operationalOnly) list = list.filter((c) => c.operational);
    if (sorted) {
      // Copy before sorting: every caller shares one cache entry, so an
      // in-place sort would reorder the array out from under a caller that
      // asked for server order.
      list = [...list].sort((a, b) => {
        if (a.stateAbbr === b.stateAbbr) return a.cityName < b.cityName ? -1 : 1;
        return a.stateAbbr < b.stateAbbr ? -1 : 1;
      });
    }
    return list;
  }, [data, operationalOnly, sorted]);

  return { cities, isLoading, error };
};

export default useCities;
